import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Coins, PlusCircle, Landmark, AlertCircle, CheckCircle2, XCircle, Loader2, Upload, ArrowUpCircle, Download } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import client from '../../api/client'
import useBetStore from '../../store/useBetStore'
import useAuthStore from '../../store/useAuthStore'
import Button from '../ui/Button'

// Función helper para compresión de imágenes en el cliente
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.75) => {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/') || file.type.includes('svg')) {
      resolve(file)
      return
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result
      img.onload = () => {
        let width = img.width
        let height = img.height

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width)
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height)
            height = maxHeight
          }
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              })
              resolve(compressedFile)
            } else {
              resolve(file)
            }
          },
          'image/jpeg',
          quality
        )
      }
      img.onerror = () => resolve(file)
    }
    reader.onerror = () => resolve(file)
  })
}

export default function RechargeModal({ isOpen, onClose }) {
  const containerRef = useRef(null)
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('recharge') // 'recharge', 'requests', 'withdrawals'
  const [monto, setMonto] = useState('')
  const [referencia, setReferencia] = useState('')
  const [rechargeFile, setRechargeFile] = useState(null)
  const [rechargePreview, setRechargePreview] = useState(null)
  const [msgExito, setMsgExito] = useState('')
  const [msgError, setMsgError] = useState('')
  
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [qrFile, setQrFile] = useState(null)
  const [qrPreview, setQrPreview] = useState(null)
  const [withdrawMsgExito, setWithdrawMsgExito] = useState('')
  const [withdrawMsgError, setWithdrawMsgError] = useState('')

  const [useSavedQr, setUseSavedQr] = useState(true)
  const [guardarQr, setGuardarQr] = useState(true)

  // Manejo de URL temporal para la vista previa del comprobante de recarga
  useEffect(() => {
    if (!rechargeFile) {
      setRechargePreview(null)
      return
    }
    const objectUrl = URL.createObjectURL(rechargeFile)
    setRechargePreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [rechargeFile])

  // Manejo de URL temporal para la vista previa del QR de retiro
  useEffect(() => {
    if (!qrFile) {
      setQrPreview(null)
      return
    }
    const objectUrl = URL.createObjectURL(qrFile)
    setQrPreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [qrFile])
  
  const {
    credits,
    recharges,
    withdrawals = [],
    isLoading,
    requestRecharge,
    fetchUserRecharges,
    requestWithdrawal,
    fetchUserWithdrawals,
    fetchBalance
  } = useBetStore()

  const { user, updateUser } = useAuthStore()

  useEffect(() => {
    if (isOpen) {
      fetchBalance().catch(() => {})
      fetchUserRecharges().catch(() => {})
      fetchUserWithdrawals().catch(() => {})
      setMsgExito('')
      setMsgError('')
      setWithdrawMsgExito('')
      setWithdrawMsgError('')
      setMonto('')
      setReferencia('')
      setRechargeFile(null)
      setRechargePreview(null)
      setWithdrawAmount('')
      setQrFile(null)
      setQrPreview(null)
      setUseSavedQr(!!user?.qr_bancario)
      setGuardarQr(true)

      // Obtener el perfil del usuario actualizado del servidor para sincronizar el QR
      client.get('/auth/profile')
        .then(res => {
          if (res.data?.datos) {
            updateUser(res.data.datos)
            setUseSavedQr(!!res.data.datos.qr_bancario)
          }
        })
        .catch(err => {
          console.error('Error al sincronizar el perfil:', err)
          setUseSavedQr(!!user?.qr_bancario)
        })
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [isOpen, activeTab])

  const handleRequest = async (e) => {
    e.preventDefault()
    setMsgExito('')
    setMsgError('')

    const val = parseFloat(monto)
    if (isNaN(val) || val <= 0) {
      setMsgError('Por favor ingresa un monto válido mayor a cero.')
      return
    }



    try {
      await requestRecharge(val, referencia, rechargeFile)
      setMsgExito('Solicitud de recarga enviada correctamente. El administrador la revisará a la brevedad.')
      setMonto('')
      setReferencia('')
      setRechargeFile(null)
      fetchUserRecharges()
      setTimeout(() => {
        onClose()
        navigate('/historial-billetera?tab=deposits')
      }, 1500)
    } catch (err) {
      setMsgError(err.response?.data?.mensaje || 'Error al procesar la solicitud.')
    }
  }

  const handleWithdraw = async (e) => {
    e.preventDefault()
    setWithdrawMsgExito('')
    setWithdrawMsgError('')

    const val = parseFloat(withdrawAmount)
    if (isNaN(val) || val <= 0) {
      setWithdrawMsgError('Por favor ingresa un monto válido mayor a cero.')
      return
    }

    if (val > credits) {
      setWithdrawMsgError(`Saldo insuficiente. Tu saldo es ${credits.toFixed(2)} Bs.`)
      return
    }

    if (!useSavedQr && !qrFile) {
      setWithdrawMsgError('Por favor selecciona la imagen de tu código QR bancario.')
      return
    }

    try {
      const res = await requestWithdrawal(val, useSavedQr ? null : qrFile, guardarQr)
      if (res?.datos?.qr_bancario) {
        updateUser({ qr_bancario: res.datos.qr_bancario })
      }
      setWithdrawMsgExito('Solicitud de retiro enviada correctamente. Los créditos se han debitado.')
      setWithdrawAmount('')
      setQrFile(null)
      setQrPreview(null)
      fetchBalance()
      fetchUserWithdrawals()
      setTimeout(() => {
        onClose()
        navigate('/historial-billetera?tab=withdrawals')
      }, 1500)
    } catch (err) {
      setWithdrawMsgError(err.response?.data?.mensaje || 'Error al enviar la solicitud.')
    }
  }

  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  // Consultar configuraciones del admin de forma pública para obtener el QR de depósito
  const { data: settings } = useQuery({
    queryKey: ['public-settings-modal'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos
    },
    enabled: isOpen
  })

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-surface-overlay backdrop-blur-sm"
        />

        {/* Modal Content */}
        <motion.div
          ref={containerRef}
          onScroll={(e) => { e.currentTarget.scrollTop = 0 }}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white dark:bg-navy-dark border border-border-default shadow-2xl z-10 flex flex-col max-h-[85vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle bg-slate-50 dark:bg-navy-dark/40">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-cyan" />
              <h2 className="font-display font-bold text-lg text-text-primary">Mi Billetera</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full bg-slate-100 dark:bg-navy-light/50 hover:bg-cyan hover:text-navy-dark transition-all text-text-primary border border-border-subtle flex items-center justify-center"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Balance Indicator */}
          <div className="p-4 bg-gradient-to-r from-cyan/10 to-blue/5 border-b border-border-subtle flex items-center justify-between">
            <span className="text-sm font-body text-text-secondary">Saldo Disponible:</span>
            <div className="flex items-center gap-1.5 font-display font-bold text-xl text-cyan">
              <Coins className="w-5 h-5 text-cyan" />
              <span>{credits.toFixed(2)} Bs</span>
            </div>
          </div>

          {/* Tabs Nav */}
          <div className="flex border-b border-border-subtle bg-slate-50 dark:bg-navy-dark/20 text-sm">
            <button
              onClick={() => setActiveTab('recharge')}
              className={`flex-1 py-3 text-center font-medium transition-colors border-b-2 flex items-center justify-center gap-2 ${
                activeTab === 'recharge'
                  ? 'border-cyan text-cyan bg-cyan/5'
                  : 'border-transparent text-text-muted hover:text-text-primary hover:bg-surface-card/30'
              }`}
            >
              <PlusCircle className="w-4 h-4" />
              Recargar
            </button>
            <button
              onClick={() => setActiveTab('withdrawals')}
              className={`flex-1 py-3 text-center font-medium transition-colors border-b-2 flex items-center justify-center gap-2 ${
                activeTab === 'withdrawals'
                  ? 'border-cyan text-cyan bg-cyan/5'
                  : 'border-transparent text-text-muted hover:text-text-primary hover:bg-surface-card/30'
              }`}
            >
              <ArrowUpCircle className="w-4 h-4" />
              Retirar
            </button>
          </div>

          {/* Scrollable Tab Content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            {activeTab === 'recharge' && (
              <form onSubmit={handleRequest} className="space-y-4">
                {settings?.qr_deposito ? (
                  <div className="flex flex-col items-center bg-slate-50 dark:bg-navy-dark/40 border border-border-subtle p-4 rounded-xl space-y-2">
                    <p className="text-xs text-text-muted font-body font-semibold">Código QR para Depósitos Bancarios / Simple</p>
                    <div className="bg-white p-3 rounded-lg border border-border-subtle shadow-inner">
                      <img
                        src={`${baseImgUrl}${settings.qr_deposito}`}
                        alt="QR Depósito Bancario"
                        className="w-40 h-40 object-contain rounded-md"
                      />
                    </div>
                    <span className="text-[10px] text-text-muted text-center">Escanea este código QR desde tu app bancaria para realizar el depósito</span>
                    <a
                      href={`${baseImgUrl}${settings.qr_deposito}`}
                      download="qr_deposito.png"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 text-xs bg-cyan/15 hover:bg-cyan/25 border border-cyan/30 text-cyan px-4 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all active:scale-95 shadow-sm"
                    >
                      <Download className="w-3.5 h-3.5" /> Descargar Código QR
                    </a>
                  </div>
                ) : (
                  <div className="bg-slate-50 dark:bg-navy-dark/45 border border-border-subtle rounded-xl p-3 text-xs text-center text-text-muted">
                    No se registra un código QR activo de depósitos en el sistema.
                  </div>
                )}

                <div className="bg-cyan/5 border border-cyan/20 rounded-xl p-3 text-xs text-cyan flex gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <p>
                    <strong>Nota:</strong> Transfiere el monto deseado a la cuenta bancaria de la casa (1 Bs = 1 Crédito). Luego, ingresa el monto, detalle de referencia y opcionalmente adjunta tu comprobante/voucher de depósito.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text-secondary">
                    Monto a Recargar (Bs)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    value={monto}
                    onChange={(e) => setMonto(e.target.value)}
                    placeholder="Ej. 100.00"
                    className="w-full bg-slate-50 dark:bg-navy-dark/50 border border-border-subtle rounded-xl px-4 py-2.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-cyan transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text-secondary">
                    Comprobante o Voucher de Depósito (Opcional)
                  </label>
                  {!rechargePreview ? (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-border-subtle hover:border-cyan/50 rounded-xl p-4 cursor-pointer bg-slate-50 dark:bg-navy-dark/40 transition-colors group">
                      <Upload className="w-8 h-8 text-text-muted group-hover:text-cyan transition-colors mb-2" />
                      <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors text-center font-medium">
                        Haz clic para subir la imagen del voucher
                      </span>
                      <span className="text-[10px] text-text-muted mt-1 font-mono">PNG, JPG, WEBP (Max. 5MB)</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0]
                          if (file) {
                            const compressed = await compressImage(file)
                            setRechargeFile(compressed)
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  ) : null}
                  {rechargePreview && (
                    <div className="mt-2 relative flex justify-center border border-border-subtle rounded-xl p-2 bg-slate-50 dark:bg-navy-dark/20">
                      <img 
                        src={rechargePreview} 
                        alt="Vista previa del voucher" 
                        className="max-h-40 max-w-full object-contain rounded-lg border border-border-default"
                      />
                      <button
                        type="button"
                        onClick={() => setRechargeFile(null)}
                        className="absolute top-2 right-2 p-1.5 rounded-lg bg-error/15 border border-error/20 hover:bg-error/30 transition-colors text-error"
                        title="Quitar comprobante"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text-secondary">
                    Referencia o Detalle de Transferencia (Opcional)
                  </label>
                  <textarea
                    rows={3}
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    placeholder="Ej: Transferencia Banco Unión Nro 493012, Titular: Juan Pérez"
                    className="w-full bg-slate-50 dark:bg-navy-dark/50 border border-border-subtle rounded-xl px-4 py-2.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-cyan transition-colors resize-none"
                  />
                </div>

                {msgExito && (
                  <div className="bg-success/10 border border-success/30 rounded-xl p-3 text-sm text-success flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <p>{msgExito}</p>
                  </div>
                )}

                {msgError && (
                  <div className="bg-error/10 border border-error/30 rounded-xl p-3 text-sm text-error flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <p>{msgError}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 py-3"
                    onClick={onClose}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 py-3"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      'Enviar Solicitud'
                    )}
                  </Button>
                </div>
              </form>
            )}



            {activeTab === 'withdrawals' && (
              <div className="space-y-4">
                {/* Formulario de Retiro */}
                <form onSubmit={handleWithdraw} className="space-y-4">
                  <div className="bg-cyan/5 border border-cyan/20 rounded-xl p-3 text-xs text-cyan flex gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <p>
                      <strong>Nota:</strong> Para solicitar un retiro, ingresa el monto y sube la imagen del código QR de tu cuenta bancaria. Los créditos serán debitados de tu saldo inmediatamente mientras el administrador procesa la transferencia.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-text-secondary">
                      Monto a Retirar (Bs)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={credits}
                      required
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="Ej. 50.00"
                      className="w-full bg-slate-50 dark:bg-navy-dark/50 border border-border-subtle rounded-xl px-4 py-2.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-cyan transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-text-secondary">
                      Imagen Código QR Bancario (QR Simple)
                    </label>

                    {useSavedQr && user?.qr_bancario ? (
                      <div className="flex flex-col items-center bg-slate-50 dark:bg-navy-dark/40 border border-border-subtle p-4 rounded-xl space-y-3 relative">
                        <div className="bg-white p-2 rounded-lg border border-border-subtle shadow-inner">
                          <img
                            src={`${baseImgUrl}${user.qr_bancario}`}
                            alt="QR Guardado"
                            className="w-32 h-32 object-contain rounded-md"
                          />
                        </div>
                        <span className="text-[11px] text-text-muted text-center font-medium">
                          Usando tu código QR guardado.
                        </span>
                        <button
                          type="button"
                          onClick={() => setUseSavedQr(false)}
                          className="text-xs bg-cyan/15 hover:bg-cyan/25 border border-cyan/35 text-cyan px-4 py-1.5 rounded-lg font-semibold transition-all active:scale-95 shadow-sm"
                        >
                          Cambiar Código QR
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {!qrPreview ? (
                          <label className="flex flex-col items-center justify-center border-2 border-dashed border-border-subtle hover:border-cyan/50 rounded-xl p-4 cursor-pointer bg-slate-50 dark:bg-navy-dark/40 transition-colors group">
                            <Upload className="w-8 h-8 text-text-muted group-hover:text-cyan transition-colors mb-2" />
                            <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors text-center font-medium">
                              Haz clic para subir tu código QR bancario
                            </span>
                            <span className="text-[10px] text-text-muted mt-1">PNG, JPG, WEBP (Max. 5MB)</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files[0]
                                if (file) {
                                  const compressed = await compressImage(file)
                                  setQrFile(compressed)
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        ) : null}
                        {qrPreview && (
                          <div className="mt-2 relative flex flex-col items-center border border-border-subtle rounded-xl p-4 bg-slate-50 dark:bg-navy-dark/20 space-y-3">
                            <div className="relative">
                              <img 
                                src={qrPreview} 
                                alt="Vista previa del código QR" 
                                className="max-h-40 max-w-full object-contain rounded-lg border border-border-default bg-white p-1"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setQrFile(null)
                                  setQrPreview(null)
                                }}
                                className="absolute -top-2 -right-2 p-1.5 rounded-lg bg-error border border-border-default hover:bg-error/80 transition-colors text-white"
                                title="Quitar código QR"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer mt-1 self-center select-none">
                              <input
                                type="checkbox"
                                checked={guardarQr}
                                onChange={(e) => setGuardarQr(e.target.checked)}
                                className="rounded bg-slate-100 dark:bg-navy-dark/50 border-border-subtle text-cyan focus:ring-0 focus:ring-offset-0 focus:outline-none w-4 h-4 cursor-pointer"
                              />
                              <span>Guardar este nuevo código QR para futuros retiros</span>
                            </label>
                          </div>
                        )}

                        {user?.qr_bancario && (
                          <button
                            type="button"
                            onClick={() => {
                              setUseSavedQr(true)
                              setQrFile(null)
                              setQrPreview(null)
                            }}
                            className="text-xs text-cyan hover:underline font-semibold text-center block w-full mt-1.5"
                          >
                            Volver a usar mi QR guardado
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {withdrawMsgExito && (
                    <div className="bg-success/10 border border-success/30 rounded-xl p-3 text-sm text-success flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      <p>{withdrawMsgExito}</p>
                    </div>
                  )}

                  {withdrawMsgError && (
                    <div className="bg-error/10 border border-error/30 rounded-xl p-3 text-sm text-error flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <p>{withdrawMsgError}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      className="w-full py-3"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2 justify-center">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enviando...
                        </span>
                      ) : (
                        'Solicitar Retiro'
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
